import React, { useContext, useState, useEffect } from "react";
import ReactTooltip from "react-tooltip";
import PropTypes from "prop-types";
import { CaretDown, CaretUp, Crown, Info } from "phosphor-react";
import { UserContext } from "context/user";
import { MapActions, MapContext } from "context/map";
import LearnMore from "components/Modal/LearnMore";
import PremiumCue from "../PremiumCue";
import "./index.scss";

const standardAlgorithms = [
  {
    title: "NDVI",
    description: "Normalized Difference Vegetation Index",
    enabled: false,
  },
  {
    title: "NDVI-R",
    description: "Regional NDVI",
    enabled: false,
  },
  {
    title: "COLOUR",
    description: "Colour",
    enabled: false,
  },
  {
    title: "NDRE",
    description: "Normalized Difference Red Edge",
    enabled: false,
    advanced: "",
  },
  {
    title: "THERMAL",
    description: "Thermal",
    enabled: false,
    advanced: "",
  },
  {
    title: "PCD",
    description: "Plant Cell Density",
    enabled: false,
    advanced: "",
  },
];
const sensorEmAlgorithms = [
  {
    title: "SHALLOW",
    description: "For shallow soil management",
    enabled: true,
  },
  {
    title: "DEEP",
    description: "For deep soil management",
    enabled: true,
  },
];

const AlgorithmListMenu = ({ menuOpened, toggleMenu }) => {
  const { profile } = useContext(UserContext);
  const { state, dispatch } = useContext(MapContext);
  const { algorithms, selectedAlgorithm, selectedLayer } = state;
  const [isOpenLearmoreModal, setIsOpenLearnMoreModal] = useState(false);
  const [algorithmDetails, setAlgorithmDetails] = useState(null);
  const [algorithmList, setAlgorithmList] = useState(standardAlgorithms);

  useEffect(() => {
    if (selectedLayer === "sensor_em") return;

    const enabledAlgorithms = ["NDVI", "NDVI-R", "COLOUR"];
    const premiumAlgorithms = ["NDRE", "THERMAL", "PCD"];

    premiumAlgorithms.map((premiumAlgorithm) => {
      const enabled = profile.feature_flags.find(
        (item) =>
          item.feature_flag === `algorithm:${premiumAlgorithm.toLowerCase()}` &&
          item.enabled
      );

      if (enabled) {
        enabledAlgorithms.push(premiumAlgorithm);
      }

      return null;
    });

    setAlgorithmList((previous) =>
      previous.map((algorithm) => ({
        ...algorithm,
        enabled:
          selectedLayer === "standard"
            ? enabledAlgorithms.includes(algorithm.title)
            : algorithms.includes(algorithm.title),
        advanced:
          selectedLayer === "standard" &&
          !enabledAlgorithms.includes(algorithm.title) ? (
            <Crown color="#09ba51" size={20} />
          ) : (
            ""
          ),
      }))
    );
  }, [algorithms, profile?.feature_flags, selectedLayer]);

  useEffect(() => {
    if (selectedLayer === "sensor_em") {
      setAlgorithmList(sensorEmAlgorithms);

      return;
    }

    setAlgorithmList(standardAlgorithms);
  }, [selectedLayer]);

  const handleSelectAlgorithm = (algorithm) => {
    if (!algorithm.enabled) return;

    dispatch({
      type: MapActions.SET_SELECTED_ALGORITHM,
      value: algorithm.title,
    });
    toggleMenu(null);
  };

  const handleViewLearnMore = (event, algorithm) => {
    const featureDetails = profile.feature_flags.find(
      (item) => item.feature_flag === `algorithm:${algorithm.toLowerCase()}`
    );
    featureDetails.modalTitle = algorithm.title;
    setAlgorithmDetails(featureDetails);
    setIsOpenLearnMoreModal(true);

    event.stopPropagation();
  };

  const renderLabel = () => {
    const label = selectedLayer === "sensor_em" ? "Depth:" : "Calculations";

    return (
      <div
        className={`menu-button ${
          menuOpened === "algorithm-list" ? "-active" : ""
        }`}
        onClick={() =>
          menuOpened === "algorithm-list"
            ? toggleMenu(null)
            : toggleMenu("algorithm-list")
        }
        aria-hidden
      >
        <label className="label">{label}</label>
        <p className="count">{selectedAlgorithm}</p>
        {menuOpened === "algorithm-list" ? (
          <CaretUp size={16} weight="fill" color="#B6B9C2" />
        ) : (
          <CaretDown size={16} weight="fill" color="#B6B9C2" />
        )}
      </div>
    );
  };

  const renderAlgorithms = () => {
    return (
      <div className="fill-list">
        {algorithmList.map((algorithm) => (
          <div
            key={algorithm.title}
            className={`fill ${
              selectedAlgorithm === algorithm.title ? "-selected" : ""
            } ${algorithm.enabled ? "-enabled" : ""}`}
            onClick={() => handleSelectAlgorithm(algorithm)}
            aria-hidden
          >
            <p className="title">
              {algorithm.title}
              {algorithm.advanced && algorithm.advanced}
            </p>
            <p className="description">{algorithm.description}</p>
            <span
              className="learn"
              onClick={(event) => handleViewLearnMore(event, algorithm.title)}
              data-tip="Learn more"
              aria-hidden
            >
              <Info size={24} color="#4d515c" />
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderListContainer = () => {
    const label =
      selectedLayer === "sensor_em" ? "Apply Depth" : "Apply Calculation";

    return (
      <div
        className={`list-container ${
          menuOpened === "algorithm-list" ? "" : "-hidden"
        }`}
      >
        <p className="container-title">{label}</p>
        {selectedLayer !== "sensor_em" && (
          <PremiumCue text="Enable Advanced calculations" />
        )}
        {renderAlgorithms()}
      </div>
    );
  };

  return (
    <>
      <ReactTooltip className="tooltip" effect="solid" />
      <div className="menu-container -algorithm" data-tour="step-4">
        <div>
          {renderLabel()}
          {renderListContainer()}
        </div>
      </div>
      {isOpenLearmoreModal && (
        <LearnMore
          isOpen={isOpenLearmoreModal}
          algorithmInfo={algorithmDetails}
          onClose={() => {
            setAlgorithmDetails(null);
            setIsOpenLearnMoreModal(false);
          }}
        />
      )}
    </>
  );
};

AlgorithmListMenu.defaultProps = {
  menuOpened: null,
};

AlgorithmListMenu.propTypes = {
  menuOpened: PropTypes.string,
  toggleMenu: PropTypes.func.isRequired,
};

export default AlgorithmListMenu;
